import { fillablePdf } from './fillable';
import { drawAgencyHeader } from './agencyInfo';

export const TP_LABELS = { SOC: 'Start of Care', ROC: 'Resumption of Care', FU: 'Follow-up (Recertification)', TRN: 'Transfer to an Inpatient Facility', DC: 'Discharge from Agency', DAH: 'Death at Home' };
export const DOC_TP = { soc_roc: 'SOC', recertification: 'FU', discharge: 'DC', oasis_assessment: 'SOC' };
const M0100_TP = { 1: 'SOC', 3: 'ROC', 4: 'FU', 5: 'FU', 6: 'TRN', 7: 'TRN', 8: 'DAH', 9: 'DC' };
const PLACEHOLDER = /\b(unknown|not (available|provided|specified|stated)|none provided)\b|^(n\/?a|none|null|-+)$/i;

export const isAnswered = (it, v) => {
  if (it.type === 'grid') {
    const cols = it.columns?.length ? it.columns : null;
    const keys = (it.rows || []).flatMap(r => (cols ? cols.map(c => `${r.code}|${c.code}`) : [r.code]));
    return Boolean(v && typeof v === 'object' && keys.every(k => v[k]));
  }
  return Array.isArray(v) ? v.length > 0 : Boolean(v);
};

export const tpFromData = (data, fallback) => {
  const m = String(Object.entries(data || {}).find(([k]) => k.startsWith('M0100'))?.[1] || '').trim()[0];
  return M0100_TP[m] || fallback;
};

// AI Document Generator output ('M1800 Grooming': '2 - text', arrays, sub-item objects) -> answers by item code
export const fromGenerated = (items, data) => {
  const out = {};
  const code = (v) => String(v ?? '').trim().split(/\s+-\s+|\s|\./)[0];
  Object.entries(data || {}).forEach(([k, v]) => {
    const it = items.find(i => k.split(' ')[0] === i.code);
    if (!it || v == null || v === '' || PLACEHOLDER.test(String(v).trim())) return;
    if (it.type === 'multi') { const m = (Array.isArray(v) ? v : [v]).map(code).filter(c => c !== '-' && it.options.some(o => o.value === c)); if (m.length) out[it.code] = m; }
    else if (it.type === 'grid' && typeof v === 'object') {
      const g = {};
      const cols = it.columns || [];
      Object.entries(v).forEach(([rk, rv]) => {
        const row = (it.rows || []).find(r => r.code === rk.trim().split(/[.\s]/)[0]);
        if (!row) return;
        if (rv && typeof rv === 'object') {
          Object.entries(rv).forEach(([ck, cv]) => {
            const col = cols.find(c => c.code === ck) || cols.find(c => c.label.toLowerCase().replace(/\s+/g, ' ').startsWith(ck.toLowerCase().slice(0, 10)))
              || (cols.length === 1 ? cols[0] : null);
            if (col && cv && code(cv) !== '-') g[`${row.code}|${col.code}`] = code(cv);
          });
        } else if (rv && code(rv) !== '-') g[cols.length ? `${row.code}|${cols[0].code}` : row.code] = code(rv);
      });
      if (Object.keys(g).length) out[it.code] = g;
    } else if (it.type === 'single') {
      const c = code(v);
      if ((it.options || []).some(o => o.value === c)) out[it.code] = c;
    } else out[it.code] = Array.isArray(v) ? v.join('; ') : typeof v === 'object' ? JSON.stringify(v) : String(v);
  });
  return out;
};

const L = 14, W = 188, GOLD = [212, 175, 55], INK = [17, 17, 17], GREY = [95, 95, 95];
const DASH_LABEL = 'Not assessed / no information';
const shortLabel = (t) => String(t || '').split(/\s[–—-]\s|:/)[0].replace(/\s+/g, ' ').trim();

export const buildOasisPdf = (tp, items, answers, info = {}) => {
  const doc = fillablePdf({ unit: 'mm', format: 'letter' });
  doc.patientInfo = { name: info.patientName, dob: info.dateOfBirth, mrn: info.mrn, dx: info.primaryDiagnosis, admission: info.startOfCareDate };
  doc.autoFill = false;
  const H = 262, LC = 34, RX = L + LC + 3, RW = W - LC - 5;
  const clean = (v) => (v && !PLACEHOLDER.test(String(v).trim()) ? String(v) : '');
  const name = clean(info.patientName), mrn = clean(info.mrn);
  let y = 0;
  const font = (size, bold = false, color = INK) => { doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(size); doc.setTextColor(...color); };
  const lines = (t, w, size, bold = false) => { font(size, bold); return doc.splitTextToSize(String(t || '').replace(/→/g, '->').replace(/[↓\u2009]/g, ' ').replace(/([a-z,])\n(?=[a-z(])/g, '$1 ').split('\n').filter(l => !/^[\s—–_-]+$/.test(l)).join('\n'), w); };
  const flat = (t) => String(t || '').replace(/\s+/g, ' ').trim();
  const pageNo = () => doc.getCurrentPageInfo().pageNumber;
  const newPage = () => {
    doc.addPage();
    font(7.5, true, GREY); doc.text(`OASIS-E2 ${tp} - ${TP_LABELS[tp]}`, L, 10);
    font(7.5, false, GREY); doc.text(`Patient: ${name || '__________'}    MRN: ${mrn || '______'}`, L + W, 10, { align: 'right' });
    doc.setDrawColor(...GOLD); doc.setLineWidth(0.5); doc.line(L, 12, L + W, 12);
    y = 16;
  };
  const ensure = (h) => { if (y + h > H) newPage(); };
  const tick = (x, yy, on, name) => {
    doc.setDrawColor(40); doc.setLineWidth(0.3); doc.rect(x, yy - 2.9, 3.4, 3.4);
    doc.addFill({ type: 'check', x, y: yy - 2.9, w: 3.4, h: 3.4, value: on, name });
  };
  const codeBox = (x, yy, val, w = 14, h = 8, name) => {
    doc.setDrawColor(40); doc.setLineWidth(0.4); doc.rect(x, yy, w, h);
    const v = String(val || '');
    doc.addFill({ type: 'text', x: x + 0.4, y: yy + 0.4, w: w - 0.8, h: h - 0.8, value: v, name, align: 'center', fontSize: w < 8 ? 10 : v.length > 3 ? 8 : 11 });
  };
  const closeBox = (s, div) => {
    const cur = pageNo();
    for (let p = s.page; p <= cur; p++) {
      doc.setPage(p);
      const top = p === s.page ? s.y : 14, bot = p === cur ? y : H + 2;
      doc.setDrawColor(120); doc.setLineWidth(0.3); doc.rect(L, top, W, bot - top);
      if (div) { const dt = p === div.page ? div.y : 14; if (dt < bot) doc.line(L + LC, dt, L + LC, bot); }
    }
    doc.setPage(cur);
  };
  const right = (txtLines, size, color = INK, bold = false) => {
    txtLines.forEach(l => { ensure(3.8); font(size, bold, color); doc.text(l, RX, y); y += size * 0.42; });
  };

  drawAgencyHeader(doc, L, 6, { size: 13 });
  y = 26;
  doc.setFillColor(...INK); doc.rect(L, y, W, 13, 'F');
  font(13, true, GOLD); doc.text(`OASIS-E2 ${TP_LABELS[tp]} (${tp})`, L + 4, y + 6);
  font(8, false, [230, 230, 230]); doc.text('Outcome and Assessment Information Set  |  CMS instrument effective 04/01/2026  |  OMB No. 0938-1279', L + 4, y + 10.5);
  y += 16;
  const meta = [['Patient Name', name], ['Date of Birth', clean(info.dateOfBirth)], ['Medical Record No.', mrn],
    ['Assessment Date (M0090)', clean(info.assessmentDate)], ['Clinician', clean(info.clinicianName)], ['Reason for Assessment (M0100)', `${tp} - ${TP_LABELS[tp]}`]];
  const cw3 = W / 3;
  meta.forEach(([k, v], i) => {
    const x = L + (i % 3) * cw3, yy = y + Math.floor(i / 3) * 11;
    doc.setDrawColor(120); doc.setLineWidth(0.3); doc.rect(x, yy, cw3, 11);
    font(6.8, true, GREY); doc.text(k.toUpperCase(), x + 2, yy + 3.6);
    doc.addFill({ type: 'text', x: x + 1.5, y: yy + 4.6, w: cw3 - 3, h: 5.6, value: v || '', name: `header_${i}`, fontSize: 9 });
  });
  y += 26;

  const missing = items.filter(it => !isAnswered(it, answers[it.code]));
  const sig = info.signature;
  doc.setDrawColor(120); doc.setLineWidth(0.3);
  doc.setFillColor(...(missing.length ? [180, 35, 24] : [21, 128, 61])); doc.rect(L, y, W, 8, 'F');
  font(10, true, [255, 255, 255]);
  doc.text(missing.length ? `ITEMS NEEDING COMPLETION: ${missing.length} of ${items.length} unanswered` : `ALL ${items.length} ITEMS ANSWERED`, L + 3, y + 5.5);
  font(8, true, [255, 255, 255]); doc.text(sig ? 'SIGNED AND LOCKED' : 'NOT SIGNED', L + W - 3, y + 5.5, { align: 'right' });
  y += 11;
  if (missing.length) {
    font(8, false, GREY); doc.text('Review every item below before the assessment is signed. Unanswered items print with empty boxes.', L + 2, y); y += 5;
    const colW = W / 2;
    const ml = missing.map(it => lines(`${it.code}.  ${it.title}`, colW - 8, 8.2));
    for (let i = 0; i < ml.length; i += 2) {
      const h = Math.max(ml[i].length, ml[i + 1]?.length || 0) * 3.6 + 1.2;
      ensure(h);
      [0, 1].forEach(c => {
        const w = ml[i + c];
        if (!w) return;
        tick(L + 2 + c * colW, y, false);
        font(8.2); w.forEach((l, k) => doc.text(l, L + 7 + c * colW, y + k * 3.6));
      });
      y += h;
    }
    y += 2;
  } else {
    font(9, false, INK); doc.text(sig ? `Signed by ${sig.name}${sig.credentials ? `, ${sig.credentials}` : ''} on ${new Date(sig.signedAt).toLocaleDateString('en-US')}.` : 'Ready for clinician signature.', L + 2, y + 1); y += 6;
  }
  newPage();

  let section = '';
  items.forEach(it => {
    const v = answers[it.code];
    if (it.section !== section) {
      section = it.section; ensure(30);
      doc.setFillColor(...INK); doc.rect(L, y, W, 7.5, 'F');
      const [sec, ...rest] = section.replace(/^Section\s+/, '').split(' ');
      font(10, true, GOLD); doc.text(`SECTION ${sec}`, L + 3, y + 5.2);
      font(10, true, [255, 255, 255]); doc.text(rest.join(' '), L + 30, y + 5.2);
      y += 10;
    }
    const title = lines(`${it.code}. ${it.title}`, W - 6, 9.5, true);
    const hdrH = title.length * 4.2 + 2.6;
    ensure(hdrH + 16);
    const s = { page: pageNo(), y };
    doc.setFillColor(236, 236, 236); doc.rect(L, y, W, hdrH, 'F');
    font(9.5, true); title.forEach((l, k) => doc.text(l, L + 2.5, y + 4.4 + k * 4.2));
    y += hdrH;
    doc.setDrawColor(120); doc.setLineWidth(0.3); doc.line(L, y, L + W, y);
    y += 4.5;
    const ins = it.type === 'grid' && it.options?.length ? String(it.instruction || '').split(/\n\s*Coding:/i)[0] : it.instruction;
    const instr = ins ? lines(ins, it.type === 'grid' ? W - 6 : RW, 7.8) : [];
    const skip = it.skip ? lines(`Skip pattern: ${it.skip}`, it.type === 'grid' ? W - 6 : RW, 7.5) : [];

    if (it.type === 'grid') {
      font(7.8, false, GREY); instr.forEach(l => { ensure(3.6); doc.text(l, L + 3, y); y += 3.3; });
      const opts = it.options || [];
      if (opts.length) {
        y += 1;
        const half = (W - 10) / 2, all = [...opts.map(o => `${o.value}.  ${shortLabel(o.label)}`), ...(it.dash ? [`-  ${DASH_LABEL}`] : [])];
        const rowsN = Math.ceil(all.length / 2);
        const wrapped = all.map(t => lines(t, half - 3, 7.5));
        const colH = (a, b) => wrapped.slice(a, b).reduce((h, w) => h + w.length * 3.2 + 0.6, 0);
        const boxH = Math.max(colH(0, rowsN), colH(rowsN, all.length)) + 5;
        ensure(boxH + 2);
        doc.setFillColor(250, 246, 230); doc.rect(L + 2, y - 3, W - 4, boxH, 'F');
        font(7.5, true); doc.text('Coding:', L + 4, y); y += 3.6;
        const ys = [y, y];
        wrapped.forEach((w, i) => { const c = i < rowsN ? 0 : 1; font(7.5); w.forEach((l, k) => doc.text(l, L + 4 + c * half, ys[c] + k * 3.2)); ys[c] += w.length * 3.2 + 0.6; });
        y += boxH - 2;
      }
      const cols = it.columns?.length ? it.columns : [{ code: '', label: 'Enter Code' }];
      const cw = cols.length > 2 ? 24 : 30, rw = W - 4 - cols.length * cw;
      const heads = cols.map(c => lines(c.label, cw - 3, 7.2, true).slice(0, 3));
      const hh = Math.max(...heads.map(h => h.length)) * 3.2 + 3;
      ensure(hh + 9);
      doc.setFillColor(242, 242, 242); doc.setDrawColor(190); doc.setLineWidth(0.2); doc.rect(L + 2, y - 2, W - 4, hh, 'FD');
      font(7.5, true); doc.text('Item', L + 4, y + 2);
      heads.forEach((h, i) => { font(7.2, true); h.forEach((l, k) => doc.text(l, L + 2 + rw + i * cw + cw / 2, y + 2 + k * 3.2, { align: 'center' })); });
      y += hh - 2;
      (it.rows || []).forEach(r => {
        const rl = lines(`${r.code}.  ${flat(r.label)}`, rw - 5, 8);
        const h = Math.max(9, rl.length * 3.5 + 4);
        ensure(h);
        doc.setDrawColor(190); doc.setLineWidth(0.2); doc.rect(L + 2, y, W - 4, h);
        font(8); rl.forEach((l, k) => doc.text(l, L + 4, y + 4.5 + k * 3.5));
        cols.forEach((c, i) => {
          const val = String(v?.[c.code ? `${r.code}|${c.code}` : r.code] || '');
          doc.setDrawColor(190); doc.line(L + 2 + rw + i * cw, y, L + 2 + rw + i * cw, y + h);
          codeBox(L + 2 + rw + i * cw + (cw - 12) / 2, y + (h - 7) / 2, val, 12, 7, `${it.code}_${r.code}${c.code ? `_${c.code}` : ''}`);
        });
        y += h;
      });
      y += 2;
      if (skip.length) { font(7.5, false, [30, 64, 175]); skip.forEach(l => { ensure(3.6); doc.text(l, L + 3, y + 1); y += 3.3; }); }
      y += 1.5;
      closeBox(s);
    } else {
      const div = { page: pageNo(), y: y - 4.5 };
      const leftY = y;
      if (it.type === 'single') {
        font(7, true, GREY); doc.text('ENTER CODE', L + LC / 2, leftY, { align: 'center' });
        codeBox(L + (LC - 14) / 2, leftY + 1.5, v || '', 14, 8, it.code);
      } else if (it.type === 'multi') {
        font(7, true, GREY); doc.text(doc.splitTextToSize('CHECK ALL THAT APPLY', LC - 4), L + LC / 2, leftY, { align: 'center' });
      } else {
        font(7, true, GREY); doc.text(it.type === 'date' ? 'ENTER DATE' : 'RESPONSE', L + LC / 2, leftY, { align: 'center' });
      }
      if (instr.length) { right(instr, 7.8, GREY); y += 2; }
      if (it.type === 'single' || it.type === 'multi') {
        const opts = [...(it.options || []), ...(it.dash ? [{ value: '-', label: DASH_LABEL }] : [])];
        opts.forEach(o => {
          const on = it.type === 'multi' ? Array.isArray(v) && v.includes(o.value) : v === o.value;
          const ol = lines(flat(o.label), RW - 16, 8.5, on);
          ensure(ol.length * 3.7 + 1.5);
          tick(RX, y, on, `${it.code}_${o.value === '-' ? 'dash' : o.value}`);
          font(8.5, true); doc.text(`${o.value}.`, RX + 5, y);
          font(8.5); ol.forEach((l, k) => doc.text(l, RX + 13, y + k * 3.7));
          y += ol.length * 3.7 + 1.6;
        });
      } else if (it.type === 'date') {
        const digits = clean(v).replace(/[^0-9]/g, '').padEnd(8, ' ').slice(0, 8).split('');
        let x = RX;
        ensure(10);
        [[0, 2, 'Month'], [2, 4, 'Day'], [4, 8, 'Year']].forEach(([a, b, lab], gi) => {
          const gx = x;
          for (let i = a; i < b; i++) { codeBox(x, y - 3, digits[i].trim(), 6, 7, `${it.code}_d${i + 1}`); x += 6; }
          font(6.5, false, GREY); doc.text(lab, gx + ((b - a) * 6) / 2, y + 7, { align: 'center' });
          if (gi < 2) { font(11, true); doc.text('/', x + 1, y + 2.2); x += 4; }
        });
        y += 8;
      } else {
        const vl = lines(clean(v) || ' ', RW - 4, 9);
        ensure(vl.length * 4 + 3);
        doc.addFill({ type: 'text', x: RX, y: y - 3.4, w: L + W - 3 - RX, h: vl.length * 4 + 0.6, value: clean(v), name: it.code, multiline: vl.length > 1 || it.type !== 'date', fontSize: 9 });
        doc.setDrawColor(150); doc.setLineWidth(0.2); doc.line(RX, y + 1.2 + (vl.length - 1) * 4, L + W - 3, y + 1.2 + (vl.length - 1) * 4);
        y += (vl.length - 1) * 4 + 3;
      }
      if (skip.length) right(skip, 7.5, [30, 64, 175]);
      y = Math.max(y, pageNo() === div.page ? leftY + (it.type === 'single' ? 9 : 2) : y) + 0.5;
      closeBox(s, div);
    }
    y += 3;
  });

  ensure(34); y += 4;
  doc.setDrawColor(120); doc.setLineWidth(0.3); doc.rect(L, y, W, 26);
  doc.setFillColor(236, 236, 236); doc.rect(L, y, W, 7, 'FD');
  font(9.5, true); doc.text('Clinician Attestation', L + 2.5, y + 4.8);
  if (sig?.image) {
    try { doc.addImage(sig.image, 'PNG', L + 6, y + 7.5, 40, 11.5); } catch { /* unreadable image */ }
    font(9, true); doc.text(`${sig.name}${sig.credentials ? `, ${sig.credentials}` : ''}`, L + 52, y + 17);
    font(9, true); doc.text(new Date(sig.signedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }), L + 132, y + 17);
  }
  if (!sig?.image) {
    doc.addFill({ type: 'text', x: L + 4, y: y + 12, w: 116, h: 6.5, value: info.clinicianName || '', name: 'clinician_signature', fontSize: 10 });
    doc.addFill({ type: 'text', x: L + 130, y: y + 12, w: W - 134, h: 6.5, value: '', name: 'clinician_signature_date', fontSize: 10 });
  }
  doc.setDrawColor(60); doc.line(L + 4, y + 19, L + 120, y + 19); doc.line(L + 130, y + 19, L + W - 4, y + 19);
  font(7.5, false, GREY); doc.text(sig ? `Electronically signed ${new Date(sig.signedAt).toLocaleString('en-US')} (password verified). Assessment locked.` : 'Signature / credentials of clinician completing the assessment', L + 4, y + 23); doc.text('Date (MM/DD/YYYY)', L + 130, y + 23);

  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i); font(7.5, false, [120, 120, 120]);
    doc.text(`OASIS-E2 ${tp}  |  ${name}`, L, 272);
    doc.text(`Page ${i} of ${n}`, L + W, 272, { align: 'right' });
  }
  return doc;
};
